/**
 * ChatBalloon — floating in-viewer contextual chat panel.
 *
 * Renders a compact chat UI anchored near the tap circle.
 * Creates or resumes a question thread on first message.
 * Receives real-time updates via Azure Web PubSub.
 *
 * UX features:
 *  - Drag only from the header bar (clear affordance with grip icon)
 *  - Resize from bottom-right corner (larger touch target on mobile)
 *  - Minimize to a small pill — keeps thread alive, unblocks the view
 *  - Close (X) fully dismisses
 */
import { useEffect, useState, useRef, useCallback } from 'react'
import { apiUrl } from '@/config'
import { getAuthHeaders } from '@/stores/authStore'
import { useAuthStore } from '@/stores/authStore'
import { useAppStore } from '@/store/appStore'
import { getGuestId, getGuestName, setGuestName } from '@/utils/guestIdentity'
import { useRealtimeChat, type LocationUpdate } from '@/hooks/useRealtimeChat'

interface ChatBalloonProps {
  shopId: string
  frameIndex: number
  imagePixelX?: number
  imagePixelY?: number
  point3D?: [number, number, number] | null
  /** Viewport position to anchor balloon near the circle */
  anchorX: number
  anchorY: number
  viewportWidth: number
  viewportHeight: number
  isOwner?: boolean
  userId?: string | null
  /** If provided, fetch this specific thread instead of create/resume flow */
  threadId?: string | null
  /** Called when thread is loaded from server — used for navigation to thread location */
  onThreadLoaded?: (thread: { frameIndex: number; imagePixelX?: number | null; imagePixelY?: number | null; point3D?: [number, number, number] | null }) => void
  /** Called when the other party sends a live location update */
  onLocationUpdate?: (loc: LocationUpdate) => void
  onClose: () => void
}

interface Thread {
  id: string
  shopId: string
  guestName: string
  status: 'open' | 'closed'
  ownerComment?: string | null
  messages: Array<{
    id: string
    senderRole: 'guest' | 'owner'
    senderName: string
    text: string
    createdAt: string
  }>
}

export function ChatBalloon({
  shopId,
  frameIndex,
  imagePixelX,
  imagePixelY,
  point3D,
  anchorX,
  anchorY,
  viewportWidth,
  viewportHeight,
  isOwner = false,
  userId,
  threadId: preloadThreadId,
  onThreadLoaded,
  onLocationUpdate,
  onClose,
}: ChatBalloonProps) {
  const guestId = getGuestId()
  // Use logged-in user's real name if available, fall back to guest name
  const authUserName = useAuthStore((s) => s.user?.name ?? null)
  const savedGuestName = authUserName || getGuestName()
  // Editable display name — guests can change it; "Visitor" means not yet set
  const [displayName, setDisplayName] = useState(savedGuestName)
  const [nameConfirmed, setNameConfirmed] = useState(savedGuestName !== 'Visitor')

  // When auth hydrates after mount, update displayName + confirm automatically
  useEffect(() => {
    if (authUserName && displayName === 'Visitor') {
      setDisplayName(authUserName)
      setNameConfirmed(true)
    }
  }, [authUserName]) // eslint-disable-line react-hooks/exhaustive-deps

  const needsName = !isOwner && !authUserName && !nameConfirmed
  const guestName = displayName

  const [thread, setThread] = useState<Thread | null>(null)
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [closingThread, setClosingThread] = useState(false)
  const [closeComment, setCloseComment] = useState('')
  const [showCloseForm, setShowCloseForm] = useState(false)
  const [isClosed, setIsClosed] = useState(false)
  const [ownerFinalComment, setOwnerFinalComment] = useState<string | null>(null)

  /** Minimized state — collapses to a small pill */
  const [minimized, setMinimized] = useState(false)

  /** Location sharing — guest can opt out */
  const [locationSharingEnabled, setLocationSharingEnabled] = useState(true)

  const messagesEndRef = useRef<HTMLDivElement>(null)

  /* ------------------------------------------------------------------
   * Size (resizable) + position
   *
   * Position is computed ONCE on mount from the anchor, then stored.
   * Drag updates pos directly. Resize only changes size — the
   * top-left corner stays pinned, so no jump/drift.
   * ------------------------------------------------------------------ */
  const MIN_W = 260
  const MIN_H = 120
  const margin = 12

  const [size, setSize] = useState({ w: 260, h: 260 })
  const balloonW = minimized ? 200 : size.w
  const balloonH = minimized ? 44 : size.h

  // Compute initial position once from anchor; store in state so it
  // doesn't shift when size changes.
  const [pos, setPos] = useState(() => {
    const initW = 260, initH = 260
    const prefLeft = anchorX - initW / 2
    const l = Math.max(margin, Math.min(prefLeft, viewportWidth - initW - margin))
    const prefTop = anchorY - initH - 20
    const rawT = prefTop < margin ? anchorY + 20 : prefTop
    const t = Math.min(rawT, viewportHeight - initH - margin)
    return { left: l, top: t }
  })

  const left = pos.left
  const top = pos.top

  /* ------------------------------------------------------------------
   * Drag-to-move (HEADER ONLY — clear affordance)
   * ------------------------------------------------------------------ */
  const dragStart = useRef<{ px: number; py: number; ol: number; ot: number } | null>(null)
  /** Track if a substantial drag happened so we can suppress click */
  const didDrag = useRef(false)

  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('button')) return // don't drag from header buttons
    e.preventDefault()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    dragStart.current = { px: e.clientX, py: e.clientY, ol: pos.left, ot: pos.top }
    didDrag.current = false
  }, [pos])

  const onHeaderPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current) return
    const dx = e.clientX - dragStart.current.px
    const dy = e.clientY - dragStart.current.py
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true
    const newL = Math.max(margin, Math.min(dragStart.current.ol + dx, viewportWidth - balloonW - margin))
    const newT = Math.max(margin, Math.min(dragStart.current.ot + dy, viewportHeight - balloonH - margin))
    setPos({ left: newL, top: newT })
  }, [viewportWidth, viewportHeight, balloonW, balloonH, margin])

  const onHeaderPointerUp = useCallback(() => { dragStart.current = null }, [])

  /* ------------------------------------------------------------------
   * Resize — top-left stays pinned, only bottom-right edge moves
   * ------------------------------------------------------------------ */
  const resizeStart = useRef<{ px: number; py: number; ow: number; oh: number } | null>(null)

  const onResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    resizeStart.current = { px: e.clientX, py: e.clientY, ow: size.w, oh: size.h }
  }, [size])

  const onResizePointerMove = useCallback((e: React.PointerEvent) => {
    if (!resizeStart.current) return
    const maxW = viewportWidth - pos.left - margin
    const maxH = viewportHeight - pos.top - margin
    const newW = Math.max(MIN_W, Math.min(maxW, resizeStart.current.ow + e.clientX - resizeStart.current.px))
    const newH = Math.max(MIN_H, Math.min(maxH, resizeStart.current.oh + e.clientY - resizeStart.current.py))
    setSize({ w: newW, h: newH })
  }, [viewportWidth, viewportHeight, pos.left, pos.top, MIN_W, MIN_H, margin])

  const onResizePointerUp = useCallback(() => { resizeStart.current = null }, [])

  /* ------------------------------------------------------------------
   * Find or create thread
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const init = async () => {
      setLoading(true)
      try {
        if (preloadThreadId) {
          const res = await fetch(
            apiUrl(`/api/shop-questions/${preloadThreadId}?shopId=${encodeURIComponent(shopId)}`),
            { headers: getAuthHeaders() }
          )
          if (res.ok) {
            const data: Thread = await res.json()
            setThread(data)
            if (data.status === 'closed') {
              setIsClosed(true)
              setOwnerFinalComment(data.ownerComment ?? null)
            }
            // Notify parent about thread location for navigation
            onThreadLoaded?.({
              frameIndex: (data as any).frameIndex ?? 0,
              imagePixelX: (data as any).imagePixelX ?? null,
              imagePixelY: (data as any).imagePixelY ?? null,
              point3D: (data as any).point3D ?? null,
            })
          }
        } else {
          const res = await fetch(apiUrl('/api/shop-questions'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              shopId,
              guestId,
              guestName,
              userId: userId ?? null,
              frameIndex,
              point3D: point3D ?? null,
              imagePixelX: imagePixelX ?? null,
              imagePixelY: imagePixelY ?? null,
            }),
          })
          if (res.ok) {
            const data: Thread = await res.json()
            setThread(data)
            if (data.status === 'closed') {
              setIsClosed(true)
              setOwnerFinalComment(data.ownerComment ?? null)
            }
          }
        }
      } catch (e) {
        console.warn('[ChatBalloon] init failed', e)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [shopId, guestId, frameIndex, preloadThreadId]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ------------------------------------------------------------------
   * Real-time chat
   * ------------------------------------------------------------------ */
  const { messages, isConnected, sendMessage, sendLocation } = useRealtimeChat({
    group: thread ? `question:${thread.id}` : '',
    userId: userId ?? undefined,
    shopId,
    initialMessages: thread?.messages ?? [],
    onClosed: (comment) => {
      setIsClosed(true)
      setOwnerFinalComment(comment ?? null)
    },
    onLocationUpdate,
  })

  /* ------------------------------------------------------------------
   * Guest: broadcast current frame + tap-indicator position to owner
   * so the owner sees exactly what the customer is pointing at.
   * ------------------------------------------------------------------ */
  const currentFrame = useAppStore((s) => s.manuallySelectedFrameIndex)
  const lastSentFrame = useRef<number | null>(null)
  const lastSentPixelX = useRef<number | undefined>(undefined)
  const lastSentPixelY = useRef<number | undefined>(undefined)
  const sentInitial = useRef(false)
  useEffect(() => {
    if (isOwner || !isConnected || currentFrame == null) return
    // Send on connect (initial), on every frame change, or when tap indicator position changes
    const posChanged =
      (imagePixelX ?? undefined) !== lastSentPixelX.current ||
      (imagePixelY ?? undefined) !== lastSentPixelY.current
    if (currentFrame === lastSentFrame.current && sentInitial.current && !posChanged) return
    lastSentFrame.current = currentFrame
    lastSentPixelX.current = imagePixelX ?? undefined
    lastSentPixelY.current = imagePixelY ?? undefined
    sentInitial.current = true
    if (!locationSharingEnabled) return
    sendLocation({
      frameIndex: currentFrame,
      imagePixelX: imagePixelX ?? undefined,
      imagePixelY: imagePixelY ?? undefined,
      point3D: point3D ?? undefined,
    })
  }, [isOwner, isConnected, currentFrame, sendLocation, imagePixelX, imagePixelY, point3D, locationSharingEnabled])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Count unread while minimized
  const lastSeenCount = useRef(0)
  const [unreadCount, setUnreadCount] = useState(0)
  useEffect(() => {
    if (minimized) {
      setUnreadCount(messages.length - lastSeenCount.current)
    } else {
      lastSeenCount.current = messages.length
      setUnreadCount(0)
    }
  }, [messages.length, minimized])

  /* ------------------------------------------------------------------
   * Send message
   * ------------------------------------------------------------------ */
  const handleSend = useCallback(async () => {
    if (!thread || !text.trim() || sending || isClosed) return
    // Block send if guest hasn't entered a name yet
    if (needsName) return
    setSending(true)
    const payload = text.trim()
    setText('')
    try {
      const role = isOwner ? 'owner' : 'guest'
      await sendMessage(payload, role, displayName)
    } catch (e) {
      console.warn('[ChatBalloon] send failed', e)
      setText(payload)
    } finally {
      setSending(false)
    }
  }, [thread, text, sending, isClosed, isOwner, sendMessage, needsName, displayName])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }, [handleSend])

  /* ------------------------------------------------------------------
   * Owner: close thread
   * ------------------------------------------------------------------ */
  const handleCloseThread = useCallback(async () => {
    if (!thread || closingThread) return
    setClosingThread(true)
    try {
      await fetch(
        apiUrl(`/api/shop-questions/${thread.id}/close?shopId=${encodeURIComponent(shopId)}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ ownerComment: closeComment.trim() || null }),
        }
      )
      setIsClosed(true)
      setOwnerFinalComment(closeComment.trim() || null)
      setShowCloseForm(false)
    } catch (e) {
      console.warn('[ChatBalloon] close thread failed', e)
    } finally {
      setClosingThread(false)
    }
  }, [thread, shopId, closeComment, closingThread])

  const formatTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  /* ------------------------------------------------------------------
   * Toggle minimize/restore — suppress if user was dragging
   * ------------------------------------------------------------------ */
  const handleHeaderClick = useCallback(() => {
    if (didDrag.current) return // was a drag, not a click
    setMinimized((m) => !m)
  }, [])

  /* ------------------------------------------------------------------
   * Render: MINIMIZED pill
   * ------------------------------------------------------------------ */
  if (minimized) {
    return (
      <div
        data-chat-balloon="true"
        style={{
          position: 'fixed',
          left: `${left}px`,
          top: `${top}px`,
          width: `${balloonW}px`,
          height: `${balloonH}px`,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '0 12px',
          backgroundColor: 'rgba(15, 15, 25, 0.92)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '22px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.45)',
          zIndex: 2999999,
          fontFamily: 'system-ui, sans-serif',
          cursor: 'grab',
          userSelect: 'none',
          transition: 'width 0.2s, height 0.2s',
        }}
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        onClick={handleHeaderClick}
      >
        {/* Chat icon */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(147,197,253,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {isOwner ? (thread?.guestName ?? guestName) : guestName}
        </span>
        {unreadCount > 0 && (
          <span style={{
            backgroundColor: 'rgba(239,68,68,0.9)',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            borderRadius: '9px',
            minWidth: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 5px',
            flexShrink: 0,
          }}>
            {unreadCount}
          </span>
        )}
        {/* Close button on minimized pill */}
        <button
          onClick={(e) => { e.stopPropagation(); onClose() }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'rgba(255,255,255,0.4)', lineHeight: 1, flexShrink: 0 }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    )
  }

  /* ------------------------------------------------------------------
   * Render: EXPANDED balloon
   * ------------------------------------------------------------------ */
  return (
    <div
      data-chat-balloon="true"
      style={{
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        width: `${balloonW}px`,
        height: `${balloonH}px`,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(15, 15, 25, 0.92)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        zIndex: 2999999,
        overflow: 'hidden',
        fontFamily: 'system-ui, sans-serif',
        transition: 'width 0.2s, height 0.2s',
      }}
    >
      {/* Header — draggable, click toggles minimize */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
          userSelect: 'none',
          cursor: 'grab',
        }}
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        onClick={handleHeaderClick}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Drag grip icon */}
          <svg width="8" height="14" viewBox="0 0 8 14" style={{ opacity: 0.35, flexShrink: 0 }}>
            <circle cx="2" cy="2" r="1.2" fill="white"/><circle cx="6" cy="2" r="1.2" fill="white"/>
            <circle cx="2" cy="7" r="1.2" fill="white"/><circle cx="6" cy="7" r="1.2" fill="white"/>
            <circle cx="2" cy="12" r="1.2" fill="white"/><circle cx="6" cy="12" r="1.2" fill="white"/>
          </svg>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(147,197,253,0.9)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 600 }}>
            {isOwner ? (thread?.guestName ?? guestName) : guestName}
          </span>
          {isClosed && (
            <span style={{ fontSize: '10px', color: 'rgba(134,239,172,0.9)', backgroundColor: 'rgba(34,197,94,0.15)', padding: '1px 6px', borderRadius: '6px' }}>
              resolved
            </span>
          )}
          {!isClosed && isConnected && (
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'rgba(74,222,128,0.9)', display: 'inline-block' }} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {/* Minimize button */}
          <button
            onClick={(e) => { e.stopPropagation(); setMinimized(true) }}
            title="Minimize"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px', color: 'rgba(255,255,255,0.45)', lineHeight: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); onClose() }}
            title="Close"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px', color: 'rgba(255,255,255,0.45)', lineHeight: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Location sharing notice — guests only */}
      {!isOwner && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '5px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexShrink: 0,
          backgroundColor: locationSharingEnabled
            ? 'rgba(34,197,94,0.07)'
            : 'rgba(255,255,255,0.04)',
        }}>
          <span style={{ fontSize: '11px', color: locationSharingEnabled ? 'rgba(134,239,172,0.8)' : 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {locationSharingEnabled ? '📍' : '🚫'}
            {locationSharingEnabled ? 'Owner can see your location' : 'Location hidden'}
          </span>
          <button
            data-no-drag
            onClick={() => setLocationSharingEnabled((v) => !v)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px 6px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 600,
              color: locationSharingEnabled ? 'rgba(248,113,113,0.85)' : 'rgba(134,239,172,0.85)',
            }}
          >
            {locationSharingEnabled ? 'Stop' : 'Allow'}
          </button>
        </div>
      )}

      {/* Messages */}
      <div data-no-drag style={{ flex: 1, overflowY: 'auto', cursor: 'auto', padding: '12px 12px 8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '12px', paddingTop: '20px' }}>
            Loading…
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '12px', paddingTop: '20px' }}>
            Ask the shop owner a question about this spot
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isMine = isOwner ? msg.senderRole === 'owner' : msg.senderRole === 'guest'
            // Show sender name for the other party's messages when sender changes
            const prevMsg = idx > 0 ? messages[idx - 1] : null
            const showName = !isMine && (!prevMsg || prevMsg.senderRole !== msg.senderRole || prevMsg.senderName !== msg.senderName)
            return (
              <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-end' : 'flex-start' }}>
                {showName && msg.senderName && (
                  <span style={{ fontSize: '10px', color: 'rgba(147,197,253,0.7)', marginBottom: '2px', paddingLeft: '4px' }}>
                    {msg.senderName}
                  </span>
                )}
                <div style={{
                  maxWidth: '80%',
                  backgroundColor: isMine ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.92)',
                  borderRadius: isMine ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  padding: '7px 10px',
                  fontSize: '13px',
                  lineHeight: 1.45,
                  wordBreak: 'break-word',
                }}>
                  {msg.text}
                </div>
                <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>
                  {formatTime(msg.createdAt)}
                </span>
              </div>
            )
          })
        )}
        {isClosed && ownerFinalComment && (
          <div style={{
            backgroundColor: 'rgba(34,197,94,0.1)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: '10px',
            padding: '8px 10px',
            fontSize: '12px',
            color: 'rgba(134,239,172,0.9)',
          }}>
            <strong>Owner note:</strong> {ownerFinalComment}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Owner: close thread form */}
      {isOwner && !isClosed && showCloseForm && (
        <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <textarea
            placeholder="Add a closing note (optional)"
            value={closeComment}
            onChange={(e) => setCloseComment(e.target.value)}
            style={{
              width: '100%',
              backgroundColor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '12px',
              padding: '6px 8px',
              resize: 'none',
              boxSizing: 'border-box',
              outline: 'none',
            }}
            rows={2}
          />
          <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
            <button onClick={() => setShowCloseForm(false)} style={{ flex: 1, padding: '5px', borderRadius: '8px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '12px' }}>
              Cancel
            </button>
            <button onClick={handleCloseThread} disabled={closingThread} style={{ flex: 1, padding: '5px', borderRadius: '8px', background: 'rgba(34,197,94,0.25)', border: '1px solid rgba(34,197,94,0.5)', color: 'rgba(134,239,172,0.9)', cursor: 'pointer', fontSize: '12px' }}>
              {closingThread ? 'Closing…' : 'Close thread'}
            </button>
          </div>
        </div>
      )}

      {/* Input bar */}
      {!isClosed ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          {/* Name prompt for unauthenticated guests */}
          {needsName && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 12px 4px',
            }}>
              <input
                type="text"
                placeholder="Your name"
                autoFocus
                value={displayName === 'Visitor' ? '' : displayName}
                onChange={(e) => setDisplayName(e.target.value.trimStart() || 'Visitor')}
                onBlur={() => {
                  if (displayName && displayName !== 'Visitor') {
                    setGuestName(displayName)
                    setNameConfirmed(true)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (displayName && displayName !== 'Visitor') {
                      setGuestName(displayName)
                      setNameConfirmed(true)
                    }
                  }
                }}
                style={{
                  flex: 1,
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(147,197,253,0.4)',
                  borderRadius: '8px',
                  color: 'rgba(255,255,255,0.88)',
                  fontSize: '13px',
                  padding: '6px 10px',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
              <span style={{ fontSize: '11px', color: 'rgba(147,197,253,0.6)', whiteSpace: 'nowrap' }}>
                Enter your name to chat
              </span>
            </div>
          )}
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            padding: '8px 12px',
          }}>
          {isOwner && !showCloseForm && (
            <button
              onClick={() => setShowCloseForm(true)}
              title="Close this thread"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'rgba(134,239,172,0.7)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </button>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message…"
            rows={1}
            style={{
              flex: 1,
              backgroundColor: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.13)',
              borderRadius: '10px',
              color: 'rgba(255,255,255,0.88)',
              fontSize: '13px',
              padding: '7px 10px',
              resize: 'none',
              outline: 'none',
              fontFamily: 'inherit',
              lineHeight: 1.4,
            }}
          />
          <button
            type="button"
            onClick={handleSend}
            onTouchEnd={(e) => { e.preventDefault(); handleSend() }}
            disabled={sending || !text.trim() || !thread || needsName}
            style={{
              flexShrink: 0,
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: text.trim() && thread ? 'rgba(59,130,246,0.8)' : 'rgba(255,255,255,0.1)',
              border: 'none',
              cursor: text.trim() && thread ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        </div>
      ) : (
        <div style={{ padding: '10px 14px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '12px', flexShrink: 0 }}>
          This conversation is closed
        </div>
      )}

      {/* Resize handle — bottom-right corner (larger touch target) */}
      <div
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 32,
          height: 32,
          cursor: 'nwse-resize',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          padding: '6px',
          zIndex: 1,
          touchAction: 'none',
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10">
          <line x1="9" y1="1" x2="1" y2="9" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="9" y1="5" x2="5" y2="9" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  )
}
